import { type ReactNode, useMemo, useState } from 'react';
import {
  type FieldValues,
  type Path,
  type PathValue,
  useFormContext,
  type UseFormReturn,
  useWatch,
} from 'react-hook-form';

import {
  ActiveFilterChips,
  type FilterDimension,
  FilterSheet,
  getActiveFilterDimensions,
} from '../../../components/FilterSheet';
import { formatListSort, type ListSort, parseListSort } from './listState';
import { MobileSearchBar, SortField, type SortOption } from './MobileSearchParts';

type WithSort<TFilters> = TFilters & { sort: string };

/** The sort order inside the filter sheet, staged with the filters until Apply. */
const StagedSortField = ({ options }: { options: SortOption[] }) => {
  const form = useFormContext<{ sort: string }>();
  const value = useWatch({ control: form.control, name: 'sort' });
  return (
    <SortField
      value={value}
      options={options}
      onChange={(next) => form.setValue('sort', next, { shouldDirty: true })}
    />
  );
};

/**
 * A search screen on phones: one search box that searches one named field as you type, Filter
 * inside it for every other field (staged until Apply, with the sort order), the active filters
 * as removable chips, then the results. The live form is the screen's existing search form, so
 * the URL sync and debounces stay exactly as they are.
 */
export const PhoneSearchLayout = <TFilters extends FieldValues>({
  form,
  searchField,
  searchPlaceholder,
  defaultFilters,
  dimensions,
  sheetTitle,
  renderFields,
  panels,
  sortOptions,
  sort,
  defaultSort,
  onSortChange,
  children,
}: {
  /** The screen's live search form (`useSearchParamsForm`). */
  form: UseFormReturn<TFilters>;
  /** The field the search box writes to. It's left out of the chips: the box shows it. */
  searchField: Path<TFilters>;
  /** Names the field, never a bare "Search": "Search by last name". */
  searchPlaceholder: string;
  defaultFilters: TFilters;
  dimensions: ReadonlyArray<FilterDimension<TFilters>>;
  sheetTitle: string;
  /** The screen's own filter fields, rendered against the staged form in the sheet. */
  renderFields?: () => ReactNode;
  /**
   * Instead of one list of fields: drill-in panels, for screens with many filters. Sort is added
   * as the last panel.
   */
  panels?: ReadonlyArray<{
    id: string;
    label: string;
    summary: (values: TFilters) => string;
    render: () => ReactNode;
  }>;
  sortOptions: SortOption[];
  sort: ListSort | null;
  defaultSort: ListSort;
  onSortChange: (sort: ListSort | null) => void;
  /** The results, given whether any search or filter is narrowing them and how to clear them. */
  children: (state: { isFiltered: boolean; clearFilters: () => void }) => ReactNode;
}) => {
  const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false);
  const watched = useWatch({ control: form.control }) as Partial<TFilters>;
  // A field missing from the URL comes through undefined rather than at its default; it isn't
  // a filter, so it mustn't count as one or show a chip.
  const values = useMemo(() => {
    const merged = { ...defaultFilters };
    for (const [key, value] of Object.entries(watched)) {
      if (value !== undefined) (merged as Record<string, unknown>)[key] = value;
    }
    return merged;
  }, [defaultFilters, watched]);
  const searchValue = (useWatch({ control: form.control, name: searchField }) as string) ?? '';

  const chipDimensions = useMemo(
    () => dimensions.filter((dimension) => !dimension.fields.includes(searchField)),
    [dimensions, searchField],
  );
  const activeFilterCount = getActiveFilterDimensions(
    chipDimensions,
    values,
    defaultFilters,
  ).length;
  const isFiltered = activeFilterCount > 0 || searchValue.trim() !== '';

  const defaultSortValue = formatListSort(defaultSort);
  const sortValue = sort ? formatListSort(sort) : defaultSortValue;

  const clearFilters = () => form.reset(defaultFilters);

  return (
    <>
      <MobileSearchBar
        value={searchValue}
        onChange={(next) =>
          form.setValue(searchField, next as PathValue<TFilters, Path<TFilters>>, {
            shouldDirty: true,
          })
        }
        placeholder={searchPlaceholder}
        activeFilterCount={activeFilterCount}
        onOpenFilters={() => setIsFilterSheetOpen(true)}
      />
      <ActiveFilterChips
        dimensions={chipDimensions}
        values={values}
        defaultValues={defaultFilters}
        onChange={(next) => form.reset(next)}
      />

      {children({ isFiltered, clearFilters })}

      <FilterSheet<WithSort<TFilters>>
        open={isFilterSheetOpen}
        onOpenChange={setIsFilterSheetOpen}
        title={sheetTitle}
        values={{ ...values, sort: sortValue }}
        defaultValues={{ ...defaultFilters, sort: defaultSortValue }}
        onApply={({ sort: nextSort, ...filters }) => {
          form.reset(filters as unknown as TFilters);
          // The default order is the absence of a sort, so it isn't written to the URL.
          onSortChange(nextSort === defaultSortValue ? null : parseListSort(nextSort));
        }}
        panels={
          panels && [
            ...panels.map((panel) => ({
              id: panel.id,
              label: panel.label,
              summary: (staged: WithSort<TFilters>) => panel.summary(staged),
              render: () => panel.render(),
            })),
            {
              id: 'sort',
              label: 'Sort by',
              summary: (staged: WithSort<TFilters>) =>
                sortOptions.find((option) => option.value === staged.sort)?.label ?? '',
              render: () => <StagedSortField options={sortOptions} />,
            },
          ]
        }
      >
        {panels
          ? undefined
          : () => (
              <>
                {renderFields?.()}
                <StagedSortField options={sortOptions} />
              </>
            )}
      </FilterSheet>
    </>
  );
};
